import { isReviewRequestLookupCommand, reviewRequestLookupCommandShapeReason, reviewRequestLookupPairReason } from './workflow-review-request-lookup-authoring-gate';
import { capabilityReferencesShapeError, sameCapabilityReference } from './workflow-capability-authoring';
import { isUnsupportedCaptureForm, captureFieldTypes } from './workflow-capture-form-validation';
import { hasInstalledPreparationLookupRuntime, hasInstalledReviewRequestLookupRuntime, isUnsupportedFormConfiguration, workflowFormFieldIssues } from './workflow-form-fields-validation';
import type { WorkflowDefinitionDocument } from './platform-workflow-definition-lifecycle.models';
import { commandIdentity, type StepAuthoringCatalog, type StepAuthoringCommand, type StepCommand } from './workflow-step-composition';
import { isWorkflowFormVersionAvailable, type WorkflowFormLayout } from '@app/shared/workflow-commands/workflow-form-layout';
import { isPreparationLookupCommand, preparationLookupCommandShapeReason } from './workflow-preparation-lookup-authoring-gate';
import { providedFormSettingsShapeReason } from './workflow-provided-lookup-authoring';

const modernFormats = ['1.10', '1.11', '1.12', '1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'];
const formKeys = new Set(['version', 'id', 'title', 'fields']);
const fieldKeys = new Set(['id', 'label', 'type', 'required', 'maxLength', 'minimum', 'maximum', 'options', 'visibleWhen', 'layout', 'lookup']);
const configurationFieldKeys = new Set(['name', 'label', 'required', 'maxLength', 'control', 'optionSource', 'options', 'layout']);
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const supportedOptions = (value: unknown): boolean => value == null || Array.isArray(value) && value.every(option => record(option)
  && Object.keys(option).every(key => key === 'value' || key === 'label') && typeof option['value'] === 'string' && typeof option['label'] === 'string');
function supportedConfiguration(value: unknown): boolean {
  if (value == null) return true;
  if (!record(value) || Object.keys(value).some(key => key !== 'version' && key !== 'fields')
    || !Array.isArray(value['fields'])) return false;
  const names = new Set<string>();
  return value['fields'].every(field => {
    if (!record(field) || Object.keys(field).some(key => !configurationFieldKeys.has(key))
      || typeof field['name'] !== 'string' || names.has(field['name'])
      || field['label'] != null && typeof field['label'] !== 'string'
      || field['required'] != null && typeof field['required'] !== 'boolean'
      || field['maxLength'] != null && typeof field['maxLength'] !== 'number'
      || field['control'] != null && !['text', 'textarea', 'select'].includes(String(field['control']))
      || field['optionSource'] != null && !['contract-options', 'step-outcomes', 'custom-options'].includes(String(field['optionSource']))
      || !supportedOptions(field['options'])) return false;
    names.add(field['name']); return true;
  });
}

/** An explicit presentation upgrade never invents earlier format prerequisites or replaces pins. */
export function formLayoutEnableReason(document: WorkflowDefinitionDocument, command: StepCommand, installed: StepAuthoringCommand | null | undefined,
  catalog?: StepAuthoringCatalog | null, nodeId?: string): string {
  const request = isReviewRequestLookupCommand(command.capability);
  if (request) {
    const reason = reviewRequestLookupCommandShapeReason(command) || reviewRequestLookupPairReason(document, nodeId ?? '', command, catalog, installed ?? undefined)
      || (!installed || !hasInstalledReviewRequestLookupRuntime(command, installed) ? 'The exact installed Request review v2 action and form are unavailable. Its saved settings are preserved.' : '');
    if (reason) return reason;
  }
  const preparation = isPreparationLookupCommand(command.capability);
  const unavailable = preparation ? preparationLookupCommandShapeReason(command) || (!installed || !hasInstalledPreparationLookupRuntime(command, installed)
    || capabilityReferencesShapeError([command.capability]) || capabilityReferencesShapeError([installed.capability])
    ? 'The exact installed Preparation v2 action and form are unavailable. Its saved settings are preserved.' : '') : '';
  if (unavailable) return unavailable;
  if (!installed || !sameCapabilityReference(command.capability, installed.capability))
    return 'The exact installed action is unavailable. Its saved form is preserved.';
  if (!modernFormats.includes(document.payloadSchemaVersion))
    return 'This saved workflow needs its Process name, shared data and format prerequisites upgraded before form layout can be enabled. Its current form is preserved.';
  if (!isWorkflowFormVersionAvailable(document.payloadSchemaVersion, command.captureForm)
    || !isWorkflowFormVersionAvailable(document.payloadSchemaVersion, command.formConfiguration))
    return 'This saved form version is unavailable in this workflow format. Its settings are preserved.';
  // A new outer format requires the declarations introduced by earlier formats.
  // Preserve invalid/unsupported sections for their owning editor, rather than filling defaults here.
  if (!['1.19', '1.20', '1.21', '1.22'].includes(document.payloadSchemaVersion)) {
    if (typeof document.displayName !== 'string' || !document.displayName.trim() || document.displayName.length > 120)
      return 'Set a Process name in General settings before enabling form layout.';
    if (!document.processData || !Array.isArray(document.processData.fields))
      return 'Restore the shared-data declaration in Data between steps before enabling form layout.';
    if (!Array.isArray(document.capabilityReferences) || !Array.isArray(document.triggers))
      return 'Restore this draft’s capability and event declarations before enabling form layout.';
    const numbering = document.numbering;
    if (!numbering || !(numbering.mode === 'disabled' && numbering.policyId == null && numbering.agencyCode == null && numbering.programCode == null
      || numbering.mode === 'required' && typeof numbering.policyId === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(numbering.policyId)
        && numbering.policyId !== '00000000-0000-0000-0000-000000000000'
        && (numbering.agencyCode == null || typeof numbering.agencyCode === 'string' && numbering.agencyCode.length <= 32)
        && (numbering.programCode == null || typeof numbering.programCode === 'string' && numbering.programCode.length <= 32)))
      return 'Restore the public-reference numbering settings before enabling form layout.';
  }
  if (installed.supportsCaptureForm) {
    const form = command.captureForm;
    if (!form || isUnsupportedCaptureForm(form) || Object.keys(form).some(key => !formKeys.has(key))
      || form.fields.some(field => Object.keys(field).some(key => !fieldKeys.has(key)) || !captureFieldTypes.some(type => type.code === field.type)
        || !supportedOptions(field.options) || field.visibleWhen != null && Object.keys(field.visibleWhen).some(key => key !== 'fieldId' && key !== 'equals')))
      return 'This saved form contains unavailable settings. Its original content is preserved.';
  } else {
    if (command.formConfiguration?.version === 3)
      return 'This saved provided-form configuration is read-only. Its settings are preserved.';
    if ((preparation || request) && (!Array.isArray(installed.formFields) || installed.formFields.some(field => !field || typeof field.name !== 'string'
      || !Array.isArray(field.options) || !Array.isArray(field.optionSources))))
      return 'The installed provided-form field settings are unavailable. Its saved form is preserved.';
    if (!installed.form || installed.form.version !== 1 || !(installed.formFields ?? []).some(field => field.supportsLayout === true))
      return 'This action does not permit field layout changes. Its provided field order and settings are preserved.';
    if (!supportedConfiguration(command.formConfiguration) || isUnsupportedFormConfiguration(command))
      return 'Restore the supported provided-form settings before enabling form layout.';
    if ((preparation || request) && ((command.formConfiguration?.fields.length ?? 0) > 50 || command.formConfiguration?.fields.some(setting => {
      const field = installed.formFields!.find(field => field.name === setting.name);
      if (!field) return true;
      const control = setting.control ?? field.control, source = setting.optionSource;
      return setting.maxLength != null && !Number.isFinite(setting.maxLength)
        || setting.layout != null && field.supportsLayout !== true
        || source != null && (control !== 'select' || !field.optionSources.includes(source))
        || setting.options != null && source !== 'custom-options'
        || field.options.length > 0 && (control !== 'select' || source != null && !['contract-options', 'step-outcomes'].includes(source));
    })))
      return 'Restore the permitted provided-form field settings before enabling form layout.';
    if (workflowFormFieldIssues(command, installed).form)
      return 'Restore the supported provided-form settings before enabling form layout.';
  }
  return '';
}

/** One local document mutation; no save, provider upgrade, execution or sample answer is involved. */
export function enableFormLayout(document: WorkflowDefinitionDocument, nodeId: string, command: StepCommand, installed: StepAuthoringCommand,
  catalog?: StepAuthoringCatalog | null): WorkflowDefinitionDocument {
  const reason = formLayoutEnableReason(document, command, installed, catalog, nodeId);
  if (reason) throw new Error(reason);
  const nodes = document.nodes.filter(node => node.id === nodeId);
  const matches = nodes.length === 1 ? nodes[0].declaration?.behavior.commands.filter(item => commandIdentity(item) === commandIdentity(command)) ?? [] : [];
  if (matches.length !== 1 || !sameCapabilityReference(matches[0].capability, command.capability) || JSON.stringify(matches[0]) !== JSON.stringify(command))
    throw new Error('The selected form changed. Reopen its current settings before enabling layout.');
  const nextCommand: StepCommand = installed.supportsCaptureForm
    ? { ...command, captureForm: { ...command.captureForm!, version: command.captureForm!.version === 1 ? 2 : command.captureForm!.version } }
    : { ...command, formConfiguration: { ...command.formConfiguration, version: 2, fields: command.formConfiguration?.fields ?? [] } };
  if (JSON.stringify(nextCommand) === JSON.stringify(command)) return document;
  return {
    ...document,
    payloadSchemaVersion: ['1.19', '1.20', '1.21', '1.22'].includes(document.payloadSchemaVersion) ? document.payloadSchemaVersion : '1.19',
    nodes: document.nodes.map(node => node.id === nodeId ? {
      ...node,
      declaration: { ...node.declaration!, behavior: { ...node.declaration!.behavior,
        commands: node.declaration!.behavior.commands.map(item => commandIdentity(item) === commandIdentity(command) ? nextCommand : item),
      } },
    } : node),
  };
}

/** A sizing gesture composes its compatible format change without an intermediate document. */
export function changeFormFieldLayout(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  command: StepCommand,
  installed: StepAuthoringCommand,
  fieldId: string,
  change: Partial<WorkflowFormLayout>,
  catalog?: StepAuthoringCatalog | null,
): WorkflowDefinitionDocument {
  if (
    !record(change) ||
    Object.keys(change).some((key) => key !== 'width' && key !== 'row') ||
    (Object.hasOwn(change, 'width') &&
      change.width !== null &&
      (typeof change.width !== 'string' ||
        !['1x', '2x', '3x', '4x', '5x', 'fill'].includes(change.width))) ||
    (Object.hasOwn(change, 'row') &&
      change.row !== null &&
      !['new', 'previous'].includes(change.row ?? ''))
  )
    throw new Error('Choose a supported field width or row placement.');
  if (
    capabilityReferencesShapeError([command.capability]) ||
    capabilityReferencesShapeError([installed.capability]) ||
    !sameCapabilityReference(command.capability, installed.capability)
  )
    throw new Error('The exact installed action is unavailable. Its saved form is preserved.');
  const nodes = document.nodes.filter((node) => node?.id === nodeId);
  const commands =
    nodes.length === 1 &&
    nodes[0].typeCode === 'human_step' &&
    Array.isArray(nodes[0].declaration?.behavior?.commands)
      ? nodes[0].declaration.behavior.commands
      : [];
  const matches = commands.filter(
    (item) => item?.capability?.key && commandIdentity(item) === commandIdentity(command),
  );
  if (
    matches.length !== 1 ||
    !sameCapabilityReference(matches[0].capability, command.capability) ||
    JSON.stringify(matches[0]) !== JSON.stringify(command)
  )
    throw new Error(
      'The selected form changed. Reopen its current settings before changing layout.',
    );
  const current = matches[0],
    capture = installed.supportsCaptureForm === true;
  const reason =
    !capture && current.formConfiguration?.version === 3
      ? providedFormSettingsShapeReason(document, current, installed) ||
        reviewRequestLookupPairReason(document, nodeId, current, catalog, installed)
      : formLayoutEnableReason(document, current, installed, catalog, nodeId);
  if (reason) throw new Error(reason);
  if (
    !modernFormats.includes(document.payloadSchemaVersion) ||
    !isWorkflowFormVersionAvailable(document.payloadSchemaVersion, current.captureForm) ||
    !isWorkflowFormVersionAvailable(document.payloadSchemaVersion, current.formConfiguration)
  )
    throw new Error(
      'This saved form version is unavailable in this workflow format. Its settings are preserved.',
    );
  let previousLayout: WorkflowFormLayout | null | undefined;
  if (capture) {
    const fields = current.captureForm?.fields.filter((field) => field.id === fieldId) ?? [];
    if (fields.length !== 1)
      throw new Error('Select one current capture field before changing layout.');
    previousLayout = fields[0].layout;
  } else {
    const fields = installed.formFields?.filter((field) => field.name === fieldId) ?? [];
    const settings =
      current.formConfiguration?.fields.filter((field) => field.name === fieldId) ?? [];
    if (fields.length !== 1 || fields[0].supportsLayout !== true || settings.length > 1)
      throw new Error('This action does not permit layout changes for the selected field.');
    previousLayout = settings[0]?.layout;
  }
  if (
    (!Object.hasOwn(change, 'width') ||
      (previousLayout?.width ?? 'fill') === (change.width ?? 'fill')) &&
    (!Object.hasOwn(change, 'row') || (previousLayout?.row ?? 'new') === (change.row ?? 'new'))
  )
    return document;
  const legacy = capture
    ? current.captureForm?.version === 1
    : current.formConfiguration == null || current.formConfiguration.version === 1;
  const candidate = legacy
    ? enableFormLayout(document, nodeId, current, installed, catalog)
    : document;
  const candidateNode = candidate.nodes.find((node) => node.id === nodeId)!;
  const candidateCommand = candidateNode.declaration!.behavior.commands.find(
    (item) => commandIdentity(item) === commandIdentity(current),
  )!;
  const layout = { ...previousLayout, ...change };
  let nextCommand: StepCommand;
  if (capture) {
    const form = candidateCommand.captureForm!;
    const nextForm = {
      ...form,
      fields: form.fields.map((field) =>
        field.id === fieldId ? { ...field, layout } : field,
      ),
    };
    if (isUnsupportedCaptureForm(nextForm))
      throw new Error(
        'This form layout has invalid row groups. Its saved fields are preserved.',
      );
    nextCommand = { ...candidateCommand, captureForm: nextForm };
  } else {
    const configuration = candidateCommand.formConfiguration!;
    const settings = configuration.fields.filter((field) => field.name === fieldId);
    const fields = settings.length
      ? configuration.fields.map((field) =>
          field.name === fieldId ? { ...field, layout } : field,
        )
      : [...configuration.fields, { name: fieldId, layout }];
    nextCommand = { ...candidateCommand, formConfiguration: { ...configuration, fields } };
    const issue =
      workflowFormFieldIssues(nextCommand, installed).form ||
      (configuration.version === 3
        ? providedFormSettingsShapeReason(candidate, nextCommand, installed)
        : '');
    if (issue) throw new Error(issue);
  }
  if (
    !isWorkflowFormVersionAvailable(candidate.payloadSchemaVersion, nextCommand.captureForm) ||
    !isWorkflowFormVersionAvailable(
      candidate.payloadSchemaVersion,
      nextCommand.formConfiguration,
    )
  )
    throw new Error(
      'This saved form version is unavailable in this workflow format. Its settings are preserved.',
    );
  return {
    ...candidate,
    nodes: candidate.nodes.map((node) =>
      node === candidateNode
        ? {
            ...node,
            declaration: {
              ...node.declaration!,
              behavior: {
                ...node.declaration!.behavior,
                commands: node.declaration!.behavior.commands.map((item) =>
                  item === candidateCommand ? nextCommand : item,
                ),
              },
            },
          }
        : node,
    ),
  };
}
